import { useState, useEffect, useCallback, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Checkbox } from "../components/ui/checkbox";
import { Textarea } from "../components/ui/textarea";
import { handleApiError, showSuccess } from "../utils/errorHandler";
import { 
  FileText, Plus, Trash2, Pencil, Eye, Download, RefreshCw, 
  Truck, Package, MapPin, Building2, Calendar, Printer
} from "lucide-react";
import { usePermissions } from "../hooks/usePermissions";
import { api } from "../App";
import { getApiPrefix } from "../utils/apiHelper";
import { useTranslation } from "../contexts/I18nContext";

export default function DDTPage() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [ddtList, setDdtList] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [spostamenti, setSpostamenti] = useState([]);
  const [selectedSpostamenti, setSelectedSpostamenti] = useState(new Set());
  const [anno, setAnno] = useState(new Date().getFullYear());
  
  // Dialog states
  const [createDialog, setCreateDialog] = useState(false);
  const [viewDialog, setViewDialog] = useState(null);
  const [cdcDialog, setCdcDialog] = useState(null);
  
  // Form state per nuovo DDT
  const [ddtForm, setDdtForm] = useState({
    causale_trasporto: "Trasferimento interno",
    mittente_id: "",
    destinatario_id: "",
    luogo_destinazione_id: "",
    trasporto_a_cura: "Interna",
    vettore: "",
    aspetto_esteriore: "Misto",
    note: ""
  });
  
  // Form CDC
  const [cdcForm, setCdcForm] = useState({
    ragione_sociale: "", indirizzo: "", citta: "", cap: "", 
    provincia: "", paese: "Italia", telefono: "", email: "", partita_iva: ""
  });
  
  // Condizioni generali vendita
  const [condizioniGenerali, setCondizioniGenerali] = useState("");
  const [condizioniLoading, setCondizioniLoading] = useState(false);
  const [condizioniDefault] = useState(`1. Non si accettano reclami trascorsi 8 giorni da ricevimento di quanto fornito; 2. La merce viaggia a rischio e pericolo del Cliente, anche se convenuta franco a destino; 3. Salvo patto espresso, tutti i prezzi si intendono franco sede della Ditta fornitrice; 4. In caso di ritardo nel pagamento decorreranno gli interessi commerciali nella misura del saggio ufficiale di sconto aumentato di tre punti; 5. Per qualsiasi controversia è competente l'autorità giudiziaria del foro della sede legale; 6. I pagamenti a rimessa diretta si devono intendere nel termine massimo di 30 gg. data fattura; 7. Non si accettano sconti ed arrotondamenti non autorizzati; 8. Con la firma del presente documento si intendono approvate tutte le Condizioni di Vendita. I Vs. dati anagrafici sono da noi considerati esatti sotto la Vs. responsabilità, salvo diversa comunicazione.

Il Vettore nell'effettuazione dei servizi di trasporto di merci su strada deve attenersi al rispetto scrupoloso delle disposizioni legislative e regolamenti poste a tutela della sicurezza della circolazione stradale e della sicurezza sociale, con particolare riferimento ai seguenti articoli del Codice della Strada: art. 61 (sagoma limite); art. 62 (massa limite); art. 142 (limiti di velocità); art. 164 (sistemazione del carico sui veicoli); art. 167 (trasporto di cose su veicoli a motore e su rimorchi); art. 174 (durata della guida degli autoveicoli adibiti al trasporto di persone e cose).`);

  // Dati intestazione azienda DDT
  const [intestazione, setIntestazione] = useState({
    nome: "", indirizzo: "", citta: "", cap: "", provincia: "",
    telefono: "", email: "", partita_iva: "", pec: "", logo_url: "",
    sede_legale_indirizzo: "", sede_legale_cap: "", sede_legale_citta: "", sede_legale_provincia: "",
    sede_secondaria_nome: "", sede_secondaria_indirizzo: "", sede_secondaria_cap: "",
    sede_secondaria_citta: "", sede_secondaria_provincia: "", sede_secondaria_telefono: ""
  });
  const [intestazioneLoading, setIntestazioneLoading] = useState(false);

  const formatDate = (d) => {
    if (!d) return "-";
    const parts = d.split("-");
    if (parts.length === 3 && parts[0].length === 4) {
      return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }
    return d;
  };

  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const prefix = getApiPrefix();
      
      const [ddtRes, ccRes, spostRes, settingsRes] = await Promise.all([
        api.get(`${prefix}/ddt?anno=${anno}`),
        api.get(`${prefix}/cost-centers`),
        api.get(`${prefix}/asset/spostamenti`),
        api.get(`${prefix}/settings`).catch(() => ({ data: {} }))
      ]);
      
      setDdtList(ddtRes.data || []);
      setCostCenters(ccRes.data || []);
      // Filtra solo spostamenti senza DDT
      const spostNoDoc = (spostRes.data || []).filter(s => !s.ddt_id);
      setSpostamenti(spostNoDoc);
      
      // Carica condizioni generali personalizzate
      if (settingsRes.data?.condizioni_generali_vendita) {
        setCondizioniGenerali(settingsRes.data.condizioni_generali_vendita);
      }
      
      // Carica dati intestazione
      if (settingsRes.data) {
        setIntestazione({
          nome: settingsRes.data.nome || "",
          indirizzo: settingsRes.data.indirizzo || "",
          citta: settingsRes.data.citta || "",
          cap: settingsRes.data.cap || "",
          provincia: settingsRes.data.provincia || "",
          telefono: settingsRes.data.telefono || "",
          email: settingsRes.data.email || "",
          partita_iva: settingsRes.data.partita_iva || "",
          pec: settingsRes.data.pec || "",
          logo_url: settingsRes.data.logo_url || "",
          sede_legale_indirizzo: settingsRes.data.sede_legale_indirizzo || "",
          sede_legale_cap: settingsRes.data.sede_legale_cap || "",
          sede_legale_citta: settingsRes.data.sede_legale_citta || "",
          sede_legale_provincia: settingsRes.data.sede_legale_provincia || "",
          sede_secondaria_nome: settingsRes.data.sede_secondaria_nome || "",
          sede_secondaria_indirizzo: settingsRes.data.sede_secondaria_indirizzo || "",
          sede_secondaria_cap: settingsRes.data.sede_secondaria_cap || "",
          sede_secondaria_citta: settingsRes.data.sede_secondaria_citta || "",
          sede_secondaria_provincia: settingsRes.data.sede_secondaria_provincia || "",
          sede_secondaria_telefono: settingsRes.data.sede_secondaria_telefono || ""
        });
      }
    } catch (err) {
      
      handleApiError(err, t("errors.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [anno, t]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Salva condizioni generali
  const handleSaveCondizioni = async () => {
    setCondizioniLoading(true);
    try {
      const prefix = getApiPrefix();
      await api.put(`${prefix}/settings/condizioni-generali?condizioni=${encodeURIComponent(condizioniGenerali)}`);
      showSuccess(t("success.conditionsSaved"));
    } catch (err) {
      
      handleApiError(err, t("errors.errorSaveConditions"));
    } finally {
      setCondizioniLoading(false);
    }
  };
  
  // Ripristina condizioni default
  const handleResetCondizioni = async () => {
    setCondizioniLoading(true);
    try {
      const prefix = getApiPrefix();
      await api.delete(`${prefix}/settings/condizioni-generali`);
      setCondizioniGenerali("");
      showSuccess(t("success.conditionsReset"));
    } catch (err) {
      
      handleApiError(err, t("errors.errorRestoreConditions"));
    } finally {
      setCondizioniLoading(false);
    }
  };

  // Salva intestazione DDT
  const handleSaveIntestazione = async () => {
    setIntestazioneLoading(true);
    try {
      const prefix = getApiPrefix();
      const params = new URLSearchParams();
      
      // Aggiungi solo i campi con valore
      Object.entries(intestazione).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      
      await api.put(`${prefix}/settings/intestazione-ddt?${params.toString()}`);
      showSuccess(t("success.ddtHeaderSaved"));
    } catch (err) {
      
      handleApiError(err, t("errors.errorSaveHeader"));
    } finally {
      setIntestazioneLoading(false);
    }
  };

  // Toggle selezione spostamento
  const toggleSpostamento = (id) => {
    const newSet = new Set(selectedSpostamenti);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setSelectedSpostamenti(newSet);
  };

  // Crea DDT
  const handleCreateDDT = async () => {
    if (selectedSpostamenti.size === 0) {
      handleApiError({ message: t("errors.selectAtLeastOneTransfer") });
      return;
    }
    
    try {
      const prefix = getApiPrefix();
      const params = new URLSearchParams();
      selectedSpostamenti.forEach(id => params.append("spostamento_ids", id));
      params.append("causale_trasporto", ddtForm.causale_trasporto);
      if (ddtForm.mittente_id) params.append("mittente_id", ddtForm.mittente_id);
      if (ddtForm.destinatario_id) params.append("destinatario_id", ddtForm.destinatario_id);
      if (ddtForm.luogo_destinazione_id) params.append("luogo_destinazione_id", ddtForm.luogo_destinazione_id);
      params.append("trasporto_a_cura", ddtForm.trasporto_a_cura);
      if (ddtForm.vettore) params.append("vettore", ddtForm.vettore);
      params.append("aspetto_esteriore", ddtForm.aspetto_esteriore);
      if (ddtForm.note) params.append("note", ddtForm.note);
      
      const res = await api.post(`${prefix}/ddt?${params.toString()}`);
      showSuccess(res.data.message);
      setCreateDialog(false);
      setSelectedSpostamenti(new Set());
      setDdtForm({
        causale_trasporto: "Trasferimento interno", mittente_id: "", destinatario_id: "",
        luogo_destinazione_id: "", trasporto_a_cura: "Interna", vettore: "", aspetto_esteriore: "Misto", note: ""
      });
      fetchData();
    } catch (err) {
      handleApiError(err, t("errors.errorCreateDdt"));
    }
  };

  // Visualizza DDT
  const handleViewDDT = async (ddtId) => {
    try {
      const prefix = getApiPrefix();
      const res = await api.get(`${prefix}/ddt/${ddtId}`);
      setViewDialog(res.data);
    } catch (err) {
      handleApiError(err, t("errors.loadDdt"));
    }
  };

  // Elimina DDT
  const handleDeleteDDT = async (ddtId) => {
    if (!confirm(t("confirm.deleteDdt"))) return;
    try {
      const prefix = getApiPrefix();
      await api.delete(`${prefix}/ddt/${ddtId}`);
      showSuccess(t("success.ddtDeleted"));
      fetchData();
    } catch (err) {
      handleApiError(err, t("errors.errorDeleteDdt"));
    }
  };

  // Scarica PDF DDT dal backend
  const handleDownloadPDF = async (ddtId, numeroDdt, anno) => {
    try {
      const prefix = getApiPrefix();
      const response = await api.get(`${prefix}/ddt/${ddtId}/pdf`, {
        responseType: 'blob'
      });
      
      // Crea URL per il blob e scarica
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `DDT_${numeroDdt}_${anno}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      showSuccess(t("success.pdfDownloaded"));
    } catch (err) {
      
      handleApiError(err, t("errors.errorDownloadPdf"));
    }
  };

  // Stampa PDF DDT - usa iframe isolato per evitare XSS
  const handlePrintDDT = (ddt) => {
    const htmlContent = generateDDTHTML(ddt);
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    document.body.appendChild(iframe);
    const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
    iframeDoc.open();
    iframeDoc.write(htmlContent);
    iframeDoc.close();
    setTimeout(() => {
      iframe.contentWindow.print();
      setTimeout(() => document.body.removeChild(iframe), 1000);
    }, 500);
  };

  // Genera HTML per stampa DDT
  const generateDDTHTML = (ddt) => {
    const righeHTML = (ddt.righe || []).map((r, i) => `
      <tr>
        <td style="border:1px solid #000;padding:8px;">${r.tipologia || '-'}</td>
        <td style="border:1px solid #000;padding:8px;">${r.numero_seriale || '-'}</td>
        <td style="border:1px solid #000;padding:8px;">${r.codice_asset || ''} - ${r.descrizione || ''}</td>
        <td style="border:1px solid #000;padding:8px;text-align:center;">${r.unita_misura || 'pz'}</td>
        <td style="border:1px solid #000;padding:8px;text-align:center;">${r.quantita || 1}</td>
      </tr>
    `).join('');

    return `
<!DOCTYPE html>
<html>
<head>
  <title>DDT n. ${ddt.numero_ddt}/${ddt.anno}</title>
  <style>
    body { font-family: Arial, sans-serif; font-size: 12px; margin: 20px; }
    .header { display: flex; justify-content: space-between; margin-bottom: 20px; }
    .mittente { width: 45%; }
    .doc-info { width: 45%; text-align: right; }
    .destinatario { display: flex; gap: 20px; margin: 20px 0; }
    .dest-box { border: 1px solid #000; padding: 10px; flex: 1; }
    table { width: 100%; border-collapse: collapse; margin: 20px 0; }
    th { background: #f0f0f0; border: 1px solid #000; padding: 8px; text-align: left; }
    .trasporto { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin: 20px 0; border: 1px solid #000; padding: 10px; }
    .trasporto-item { }
    .trasporto-item label { font-weight: bold; display: block; margin-bottom: 4px; }
    .firme { display: flex; justify-content: space-between; margin-top: 40px; }
    .firma-box { width: 30%; border-top: 1px solid #000; padding-top: 10px; text-align: center; }
    h1 { margin: 0; font-size: 18px; }
    h2 { margin: 0 0 10px 0; font-size: 14px; }
  </style>
</head>
<body>
  <div class="header">
    <div class="mittente">
      <h2>${ddt.mittente_ragione_sociale || ddt.mittente_nome || 'MITTENTE'}</h2>
      <p>${ddt.mittente_indirizzo || ''}</p>
      <p>${ddt.mittente_cap || ''} ${ddt.mittente_citta || ''} ${ddt.mittente_provincia ? `(${ddt.mittente_provincia})` : ''}</p>
      <p>Tel: ${ddt.mittente_telefono || ''}</p>
      <p>P.IVA: ${ddt.mittente_piva || ''}</p>
    </div>
    <div class="doc-info">
      <h1>{t("nav.ddtRegistry")}</h1>
      <p><strong>N.</strong> ${ddt.numero_ddt} del ${formatDate(ddt.data_ddt)}</p>
      <p><strong>Causale:</strong> ${ddt.causale_trasporto}</p>
    </div>
  </div>

  <div class="destinatario">
    <div class="dest-box">
      <strong>DESTINATARIO</strong><br/>
      ${ddt.destinatario_ragione_sociale || ddt.destinatario_nome || '-'}<br/>
      ${ddt.destinatario_indirizzo || ''}<br/>
      ${ddt.destinatario_cap || ''} ${ddt.destinatario_citta || ''} ${ddt.destinatario_provincia ? `(${ddt.destinatario_provincia})` : ''}
    </div>
    <div class="dest-box">
      <strong>LUOGO DI DESTINAZIONE</strong><br/>
      ${ddt.luogo_ragione_sociale || ddt.luogo_nome || '-'}<br/>
      ${ddt.luogo_indirizzo || ''}<br/>
      ${ddt.luogo_cap || ''} ${ddt.luogo_citta || ''} ${ddt.luogo_provincia ? `(${ddt.luogo_provincia})` : ''}
    </div>
  </div>

  <table>
    <thead>
      <tr>
        <th style="width:15%">{t('common.typology')}</th>
        <th style="width:15%">N° Seriale</th>
        <th style="width:50%">Asset / Descrizione</th>
        <th style="width:10%">U.M.</th>
        <th style="width:10%">Q.tà</th>
      </tr>
    </thead>
    <tbody>
      ${righeHTML}
    </tbody>
  </table>

  <div class="trasporto">
    <div class="trasporto-item">
      <label>{t('common.transportBy')}</label>
      <span>${ddt.trasporto_a_cura || '-'}</span>
    </div>
    <div class="trasporto-item">
      <label>{t('common.carrier')}</label>
      <span>${ddt.vettore || '-'}</span>
    </div>
    <div class="trasporto-item">
      <label>Aspetto esteriore</label>
      <span>${ddt.aspetto_esteriore || '-'}</span>
    </div>
    <div class="trasporto-item">
      <label>N. Colli</label>
      <span>${ddt.totale_colli || '-'}</span>
    </div>
    <div class="trasporto-item">
      <label>Peso Netto (kg)</label>
      <span>${ddt.peso_netto || '-'}</span>
    </div>
    <div class="trasporto-item">
      <label>Peso Lordo (kg)</label>
      <span>${ddt.peso_lordo || '-'}</span>
    </div>
    <div class="trasporto-item">
      <label>Data inizio trasporto</label>
      <span>${ddt.data_inizio_trasporto || '-'}</span>
    </div>
    <div class="trasporto-item">
      <label>{t('common.now')}</label>
      <span>${ddt.ora_inizio_trasporto || '-'}</span>
    </div>
  </div>

  ${ddt.note ? `<p><strong>Note:</strong> ${ddt.note}</p>` : ''}
  ${ddt.annotazioni ? `<p><strong>Annotazioni:</strong> ${ddt.annotazioni}</p>` : ''}

  <div class="firme">
    <div class="firma-box">
      <p>Firma conducente</p>
    </div>
    <div class="firma-box">
      <p>Firma vettore</p>
    </div>
    <div class="firma-box">
      <p>Firma destinatario</p>
    </div>
  </div>
</body>
</html>`;
  };

  // Aggiorna indirizzo CDC
  const handleUpdateCDC = async () => {
    if (!cdcDialog) return;
    try {
      const prefix = getApiPrefix();
      const params = new URLSearchParams();
      Object.entries(cdcForm).forEach(([k, v]) => {
        if (v) params.append(k, v);
      });
      await api.put(`${prefix}/cost-centers/${cdcDialog.id}/indirizzo?${params.toString()}`);
      showSuccess(t("success.cdcAddressUpdated"));
      setCdcDialog(null);
      fetchData();
    } catch (err) {
      handleApiError(err, t("errors.errorUpdateCdc"));
    }
  };

  // Apri dialog modifica CDC
  const openCdcDialog = (cc) => {
    setCdcForm({
      ragione_sociale: cc.ragione_sociale || "",
      indirizzo: cc.indirizzo || "",
      citta: cc.citta || "",
      cap: cc.cap || "",
      provincia: cc.provincia || "",
      paese: cc.paese || "Italia",
      telefono: cc.telefono || "",
      email: cc.email || "",
      partita_iva: cc.partita_iva || ""
    });
    setCdcDialog(cc);
  };

  return (
    <div className="space-y-6" data-testid="ddt-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
              <FileText className="h-5 w-5 text-white" />
            </div>
            Registro DDT
          </h1>
          <p className="text-muted-foreground text-sm mt-1">Documenti di Trasporto per spostamenti asset</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchData} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />{t('common.refresh')}</Button>
          <Button onClick={() => setCreateDialog(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Nuovo DDT
          </Button>
        </div>
      </div>

      <Tabs defaultValue="registro" className="space-y-4">
        <TabsList>
          <TabsTrigger value="registro">
            <FileText className="h-4 w-4 mr-2" />
            Registro DDT
          </TabsTrigger>
          <TabsTrigger value="cdc">
            <Building2 className="h-4 w-4 mr-2" />
            Anagrafiche CDC
          </TabsTrigger>
          <TabsTrigger value="impostazioni">
            <Pencil className="h-4 w-4 mr-2" />
            Impostazioni DDT
          </TabsTrigger>
        </TabsList>

        {/* Tab Registro DDT */}
        <TabsContent value="registro" className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex justify-between items-center">
                <CardTitle className="text-base">DDT Emessi</CardTitle>
                <Select value={String(anno)} onValueChange={(v) => setAnno(parseInt(v))}>
                  <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[2024, 2025, 2026, 2027].map(y => (
                      <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <p className="text-center py-8 text-muted-foreground">{t("common.loading")}</p>
              ) : ddtList.length === 0 ? (
                <p className="text-center py-8 text-muted-foreground">Nessun DDT per l'anno {anno}</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>N. DDT</TableHead>
                      <TableHead>{t("common.date")}</TableHead>
                      <TableHead>Causale</TableHead>
                      <TableHead>{t('common.recipient')}</TableHead>
                      <TableHead>Righe</TableHead>
                      <TableHead className="text-right">{t("common.actions")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {ddtList.map(ddt => (
                      <TableRow key={ddt.id}>
                        <TableCell className="font-mono font-medium">{ddt.numero_ddt}/{ddt.anno}</TableCell>
                        <TableCell>{formatDate(ddt.data_ddt)}</TableCell>
                        <TableCell>{ddt.causale_trasporto}</TableCell>
                        <TableCell>{ddt.destinatario_ragione_sociale || ddt.destinatario_nome || '-'}</TableCell>
                        <TableCell><Badge variant="secondary">{ddt.num_righe}</Badge></TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="icon" onClick={() => handleViewDDT(ddt.id)}>
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDownloadPDF(ddt.id, ddt.numero_ddt, ddt.anno)}>
                            <Download className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleViewDDT(ddt.id).then(() => viewDialog && handlePrintDDT(viewDialog))}>
                            <Printer className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="text-red-600" onClick={() => handleDeleteDDT(ddt.id)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab Anagrafiche CDC */}
        <TabsContent value="cdc" className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Centri di Costo - Indirizzi per DDT</CardTitle>
              <CardDescription>Configura ragione sociale e indirizzo per ogni CDC da usare nei DDT</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("common.code")}</TableHead>
                    <TableHead>{t("common.name")}</TableHead>
                    <TableHead>{t('common.company')}</TableHead>
                    <TableHead>{t('common.address')}</TableHead>
                    <TableHead>{t('common.city')}</TableHead>
                    <TableHead className="text-right">{t("common.actions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {costCenters.map(cc => (
                    <TableRow key={cc.id}>
                      <TableCell className="font-mono">{cc.codice}</TableCell>
                      <TableCell className="font-medium">{cc.nome}</TableCell>
                      <TableCell>{cc.ragione_sociale || <span className="text-muted-foreground">-</span>}</TableCell>
                      <TableCell>{cc.indirizzo || <span className="text-muted-foreground">-</span>}</TableCell>
                      <TableCell>{cc.citta || <span className="text-muted-foreground">-</span>}</TableCell>
                      <TableCell className="text-right">
                        <Button variant="outline" size="sm" onClick={() => openCdcDialog(cc)}>
                          <Pencil className="h-4 w-4 mr-1" />{t('common.edit')}</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab Impostazioni DDT */}
        <TabsContent value="impostazioni" className="space-y-4">
          {/* Intestazione Azienda DDT */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                Intestazione Azienda DDT
              </CardTitle>
              <CardDescription>
                Configura i dati aziendali che appariranno nell'intestazione del DDT.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Dati Azienda Principali */}
              <div>
                <h4 className="text-sm font-semibold mb-3">Dati Azienda</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-xs">{t('common.company')}</Label>
                    <Input 
                      value={intestazione.nome} 
                      onChange={(e) => setIntestazione({...intestazione, nome: e.target.value})}
                      placeholder="Nome azienda"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Partita IVA</Label>
                    <Input 
                      value={intestazione.partita_iva} 
                      onChange={(e) => setIntestazione({...intestazione, partita_iva: e.target.value})}
                      placeholder="IT12345678901"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">{t('common.phone')}</Label>
                    <Input 
                      value={intestazione.telefono} 
                      onChange={(e) => setIntestazione({...intestazione, telefono: e.target.value})}
                      placeholder="+39 000 0000000"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">{t('auth.email')}</Label>
                    <Input 
                      value={intestazione.email} 
                      onChange={(e) => setIntestazione({...intestazione, email: e.target.value})}
                      placeholder="info@azienda.it"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">PEC</Label>
                    <Input 
                      value={intestazione.pec} 
                      onChange={(e) => setIntestazione({...intestazione, pec: e.target.value})}
                      placeholder="azienda@pec.it"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">URL Logo</Label>
                    <Input 
                      value={intestazione.logo_url} 
                      onChange={(e) => setIntestazione({...intestazione, logo_url: e.target.value})}
                      placeholder="https://..."
                    />
                  </div>
                </div>
              </div>

              {/* Sede Legale */}
              <div>
                <h4 className="text-sm font-semibold mb-3">Sede Legale Amministrativa</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="col-span-2">
                    <Label className="text-xs">{t('common.address')}</Label>
                    <Input 
                      value={intestazione.sede_legale_indirizzo} 
                      onChange={(e) => setIntestazione({...intestazione, sede_legale_indirizzo: e.target.value})}
                      placeholder="Via/Piazza..."
                    />
                  </div>
                  <div>
                    <Label className="text-xs">CAP</Label>
                    <Input 
                      value={intestazione.sede_legale_cap} 
                      onChange={(e) => setIntestazione({...intestazione, sede_legale_cap: e.target.value})}
                      placeholder="00000"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">{t('common.city')}</Label>
                    <Input 
                      value={intestazione.sede_legale_citta} 
                      onChange={(e) => setIntestazione({...intestazione, sede_legale_citta: e.target.value})}
                      placeholder={t("common.city")}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Provincia</Label>
                    <Input 
                      value={intestazione.sede_legale_provincia} 
                      onChange={(e) => setIntestazione({...intestazione, sede_legale_provincia: e.target.value})}
                      placeholder="XX"
                      maxLength={2}
                    />
                  </div>
                </div>
              </div>

              {/* Sede Secondaria */}
              <div>
                <h4 className="text-sm font-semibold mb-3">Sede Secondaria (opzionale)</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="col-span-2">
                    <Label className="text-xs">Nome/Descrizione</Label>
                    <Input 
                      value={intestazione.sede_secondaria_nome} 
                      onChange={(e) => setIntestazione({...intestazione, sede_secondaria_nome: e.target.value})}
                      placeholder="es. Magazzino, Stabilimento..."
                    />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs">{t('common.address')}</Label>
                    <Input 
                      value={intestazione.sede_secondaria_indirizzo} 
                      onChange={(e) => setIntestazione({...intestazione, sede_secondaria_indirizzo: e.target.value})}
                      placeholder="Via/Piazza..."
                    />
                  </div>
                  <div>
                    <Label className="text-xs">CAP</Label>
                    <Input 
                      value={intestazione.sede_secondaria_cap} 
                      onChange={(e) => setIntestazione({...intestazione, sede_secondaria_cap: e.target.value})}
                      placeholder="00000"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">{t('common.city')}</Label>
                    <Input 
                      value={intestazione.sede_secondaria_citta} 
                      onChange={(e) => setIntestazione({...intestazione, sede_secondaria_citta: e.target.value})}
                      placeholder={t("common.city")}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Provincia</Label>
                    <Input 
                      value={intestazione.sede_secondaria_provincia} 
                      onChange={(e) => setIntestazione({...intestazione, sede_secondaria_provincia: e.target.value})}
                      placeholder="XX"
                      maxLength={2}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">{t('common.phone')}</Label>
                    <Input 
                      value={intestazione.sede_secondaria_telefono} 
                      onChange={(e) => setIntestazione({...intestazione, sede_secondaria_telefono: e.target.value})}
                      placeholder="+39 000 0000000"
                    />
                  </div>
                </div>
              </div>

              <Button 
                onClick={handleSaveIntestazione} 
                disabled={intestazioneLoading}
              >
                {intestazioneLoading ? t("common.saving") : t("common.save")}
              </Button>
            </CardContent>
          </Card>

          {/* Condizioni Generali di Vendita */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Pencil className="h-4 w-4" />
                Condizioni Generali di Vendita
              </CardTitle>
              <CardDescription>
                Personalizza le condizioni generali di vendita che appariranno in fondo al DDT.
                Se lasci vuoto, verranno usate le condizioni standard.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-sm font-medium mb-2 block">
                  Testo condizioni personalizzate
                </Label>
                <Textarea
                  value={condizioniGenerali}
                  onChange={(e) => setCondizioniGenerali(e.target.value)}
                  placeholder={condizioniDefault}
                  className="min-h-[200px] font-mono text-xs"
                />
                <p className="text-xs text-muted-foreground mt-2">
                  {condizioniGenerali ? `${condizioniGenerali.length} caratteri` : "Usando condizioni standard"}
                </p>
              </div>
              
              <div className="flex gap-2">
                <Button 
                  onClick={handleSaveCondizioni} 
                  disabled={condizioniLoading || !condizioniGenerali}
                >
                  {condizioniLoading ? t("common.saving") : t("common.save")}
                </Button>
                <Button 
                  variant="outline" 
                  onClick={handleResetCondizioni}
                  disabled={condizioniLoading || !condizioniGenerali}
                >
                  Ripristina Default
                </Button>
              </div>
              
              <div className="mt-6 p-4 bg-muted rounded-lg">
                <h4 className="text-sm font-semibold mb-2">Anteprima condizioni standard:</h4>
                <p className="text-xs text-muted-foreground whitespace-pre-wrap">
                  {condizioniDefault}
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialog Creazione DDT */}
      <Dialog open={createDialog} onOpenChange={setCreateDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Nuovo DDT
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Selezione spostamenti */}
            <div>
              <Label className="text-sm font-semibold mb-2 block">Seleziona Spostamenti da includere</Label>
              {spostamenti.length === 0 ? (
                <p className="text-muted-foreground text-sm py-4">Nessuno spostamento disponibile (senza DDT)</p>
              ) : (
                <div className="border rounded-lg max-h-60 overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12"></TableHead>
                        <TableHead>{t('assets.title')}</TableHead>
                        <TableHead>Da</TableHead>
                        <TableHead>A</TableHead>
                        <TableHead>{t("common.date")}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {spostamenti.map(s => (
                        <TableRow key={s.id} className={selectedSpostamenti.has(s.id) ? "bg-blue-50" : ""}>
                          <TableCell>
                            <Checkbox 
                              checked={selectedSpostamenti.has(s.id)}
                              onCheckedChange={() => toggleSpostamento(s.id)}
                            />
                          </TableCell>
                          <TableCell className="font-medium">{s.asset_codice} - {s.asset_descrizione}</TableCell>
                          <TableCell>{s.da_cdc_nome || '-'}</TableCell>
                          <TableCell>{s.a_cdc_nome || '-'}</TableCell>
                          <TableCell>{formatDate(s.data_spostamento)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
              <p className="text-sm text-muted-foreground mt-1">
                {selectedSpostamenti.size} spostamenti selezionati
              </p>
            </div>

            {/* Dettagli DDT */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('common.transportReason')}</Label>
                <Input 
                  value={ddtForm.causale_trasporto}
                  onChange={(e) => setDdtForm({...ddtForm, causale_trasporto: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('common.transportBy')}</Label>
                <Select value={ddtForm.trasporto_a_cura} onValueChange={(v) => setDdtForm({...ddtForm, trasporto_a_cura: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Interna">{t('common.internal')}</SelectItem>
                    <SelectItem value="Mittente">{t('common.sender')}</SelectItem>
                    <SelectItem value="Destinatario">{t('common.recipient')}</SelectItem>
                    <SelectItem value="Vettore">{t('common.carrier')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Mittente (CDC)</Label>
                <Select value={ddtForm.mittente_id} onValueChange={(v) => setDdtForm({...ddtForm, mittente_id: v})}>
                  <SelectTrigger><SelectValue placeholder={t("common.selectPlaceholder")} /></SelectTrigger>
                  <SelectContent>
                    {costCenters.map(cc => (
                      <SelectItem key={cc.id} value={cc.id}>{cc.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Destinatario (CDC)</Label>
                <Select value={ddtForm.destinatario_id} onValueChange={(v) => setDdtForm({...ddtForm, destinatario_id: v})}>
                  <SelectTrigger><SelectValue placeholder={t("common.selectPlaceholder")} /></SelectTrigger>
                  <SelectContent>
                    {costCenters.map(cc => (
                      <SelectItem key={cc.id} value={cc.id}>{cc.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Luogo di destinazione (CDC)</Label>
                <Select value={ddtForm.luogo_destinazione_id} onValueChange={(v) => setDdtForm({...ddtForm, luogo_destinazione_id: v})}>
                  <SelectTrigger><SelectValue placeholder={t("common.selectPlaceholder")} /></SelectTrigger>
                  <SelectContent>
                    {costCenters.map(cc => (
                      <SelectItem key={cc.id} value={cc.id}>{cc.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('common.appearance')}</Label>
                <Input 
                  value={ddtForm.aspetto_esteriore}
                  onChange={(e) => setDdtForm({...ddtForm, aspetto_esteriore: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('common.carrier')}</Label>
                <Input 
                  value={ddtForm.vettore}
                  onChange={(e) => setDdtForm({...ddtForm, vettore: e.target.value})}
                  placeholder="Nome vettore (opzionale)"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t("common.notes")}</Label>
              <Textarea 
                value={ddtForm.note}
                onChange={(e) => setDdtForm({...ddtForm, note: e.target.value})}
                placeholder={t("common.notes")}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialog(false)}>{t("common.cancel")}</Button>
            <Button onClick={handleCreateDDT} disabled={selectedSpostamenti.size === 0}>
              <FileText className="h-4 w-4 mr-2" />
              Crea DDT ({selectedSpostamenti.size})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Visualizza DDT */}
      <Dialog open={!!viewDialog} onOpenChange={() => setViewDialog(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              DDT n. {viewDialog?.numero_ddt}/{viewDialog?.anno}
            </DialogTitle>
          </DialogHeader>
          
          {viewDialog && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-xs text-muted-foreground">{t("common.date")}</p>
                  <p className="font-medium">{formatDate(viewDialog.data_ddt)}</p>
                </div>
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-xs text-muted-foreground">Causale</p>
                  <p className="font-medium">{viewDialog.causale_trasporto}</p>
                </div>
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-xs text-muted-foreground">Colli</p>
                  <p className="font-medium">{viewDialog.totale_colli || '-'}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="border rounded-lg p-3">
                  <p className="text-xs font-semibold text-muted-foreground mb-1">MITTENTE</p>
                  <p className="font-medium">{viewDialog.mittente_ragione_sociale || viewDialog.mittente_nome || '-'}</p>
                  <p className="text-sm">{viewDialog.mittente_indirizzo}</p>
                  <p className="text-sm">{viewDialog.mittente_cap} {viewDialog.mittente_citta}</p>
                </div>
                <div className="border rounded-lg p-3">
                  <p className="text-xs font-semibold text-muted-foreground mb-1">DESTINATARIO</p>
                  <p className="font-medium">{viewDialog.destinatario_ragione_sociale || viewDialog.destinatario_nome || '-'}</p>
                  <p className="text-sm">{viewDialog.destinatario_indirizzo}</p>
                  <p className="text-sm">{viewDialog.destinatario_cap} {viewDialog.destinatario_citta}</p>
                </div>
              </div>

              <div>
                <p className="text-sm font-semibold mb-2">Righe DDT</p>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('common.typology')}</TableHead>
                      <TableHead>N. Seriale</TableHead>
                      <TableHead>{t('assets.title')}</TableHead>
                      <TableHead>U.M.</TableHead>
                      <TableHead>Q.tà</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(viewDialog.righe || []).map((r, i) => (
                      <TableRow key={r.id || `ddt-row-${r.codice_asset}-${i}`}>
                        <TableCell>{r.tipologia || '-'}</TableCell>
                        <TableCell className="font-mono">{r.numero_seriale || '-'}</TableCell>
                        <TableCell>{r.codice_asset} - {r.descrizione}</TableCell>
                        <TableCell>{r.unita_misura || 'pz'}</TableCell>
                        <TableCell>{r.quantita || 1}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setViewDialog(null)}>{t("common.close")}</Button>
            <Button variant="outline" onClick={() => handleDownloadPDF(viewDialog.id, viewDialog.numero_ddt, viewDialog.anno)}>
              <Download className="h-4 w-4 mr-2" />
              Scarica PDF
            </Button>
            <Button onClick={() => handlePrintDDT(viewDialog)}>
              <Printer className="h-4 w-4 mr-2" />{t('common.print')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Modifica CDC */}
      <Dialog open={!!cdcDialog} onOpenChange={() => setCdcDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("costCenters.editAddress")}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{t('common.company')}</Label>
              <Input value={cdcForm.ragione_sociale} onChange={(e) => setCdcForm({...cdcForm, ragione_sociale: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>{t('common.address')}</Label>
              <Input value={cdcForm.indirizzo} onChange={(e) => setCdcForm({...cdcForm, indirizzo: e.target.value})} />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className="space-y-2">
                <Label>CAP</Label>
                <Input value={cdcForm.cap} onChange={(e) => setCdcForm({...cdcForm, cap: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>{t('common.city')}</Label>
                <Input value={cdcForm.citta} onChange={(e) => setCdcForm({...cdcForm, citta: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Prov.</Label>
                <Input value={cdcForm.provincia} onChange={(e) => setCdcForm({...cdcForm, provincia: e.target.value})} maxLength={2} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-2">
                <Label>{t('common.phone')}</Label>
                <Input value={cdcForm.telefono} onChange={(e) => setCdcForm({...cdcForm, telefono: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>{t('auth.email')}</Label>
                <Input value={cdcForm.email} onChange={(e) => setCdcForm({...cdcForm, email: e.target.value})} type="email" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Partita IVA</Label>
              <Input value={cdcForm.partita_iva} onChange={(e) => setCdcForm({...cdcForm, partita_iva: e.target.value})} />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCdcDialog(null)}>{t("common.cancel")}</Button>
            <Button onClick={handleUpdateCDC}>{t("common.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
